import { curSym } from "@/lib/company";
import { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useActiveLocation } from "@/context/ActiveLocationContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { FirmLogo, BrandLogo } from "@/components/BrandLogo";
import { EntityAvatar, fileUrl } from "@/components/EntityAvatar";
import NotificationBell from "@/components/NotificationBell";
import { SecurityDialog } from "@/components/SecurityDialog";
import MaintenanceBanner from "@/components/MaintenanceBanner";
import CommandPalette from "@/components/CommandPalette";
import {
  LayoutDashboard, Package, PackagePlus, ShoppingCart, ReceiptText,
  Users, LogOut, Menu, X, Sparkles, FolderTree, ShieldCheck, ShieldAlert, Coins, BarChart3, Contact, Bot, Plug, MapPin, Boxes, ArrowLeftRight, ClipboardCheck, MessageCircle, CircleDollarSign, Truck, ChevronDown, FileText, Landmark, PartyPopper, Cpu, Trophy, PackageX, MessageSquareText, Settings2, Archive, Tag, ShoppingBag, Building2,
  History, Percent, Activity, CalendarClock, Calculator, Route, Search,
} from "lucide-react";
import { SECTOR_GATED_ROUTES } from "@/lib/sectors";
import { isMarketFamilySector } from "@/lib/sectorVisibility";
import { hiddenRoutesFromFeatures } from "@/lib/features";

const SECTOR_GATED_SET = new Set(SECTOR_GATED_ROUTES);

const NAV_GROUPS = [
  { title: null, items: [
    { to: "/", label: "Panel", icon: LayoutDashboard, perm: "dashboard.read", end: true },
  ] },
  { title: "Katalog & Fiyat", items: [
    { to: "/urunler", label: "Ürün Kartları", icon: Package, perm: "price.read" },
    { to: "/katalog-sagligi", label: "Katalog Sağlığı", icon: Activity, perm: "price.read" },
    { to: "/fiyat-yonetimi", label: "Fiyat Yönetimi", icon: CircleDollarSign, perm: "price.read" },
    { to: "/kategoriler", label: "Kategoriler", icon: FolderTree, perm: "category.manage" },
  ] },
  { title: "Stok & Lojistik", items: [
    { to: "/stok", label: "Stok", icon: Boxes, perm: "stock.read" },
    { to: "/sayim", label: "Depo Sayımı", icon: ClipboardCheck, perm: "stock.read" },
    { to: "/fire", label: "Fire / Zayiat", icon: PackageX, perm: "stock.adjust" },
    { to: "/lojistik", label: "Lojistik / Sevkiyat", icon: Truck, perm: "logistics.read" },
  ] },
  { title: "Satın Alma & Tedarik", items: [
    { to: "/satin-alma", label: "Satın Alma", icon: ShoppingBag, perm: "transfer.read" },
    { to: "/transferler", label: "Transferler & Siparişler", icon: ArrowLeftRight, perm: "transfer.read" },
    { to: "/mal-kabul", label: "Mal Kabul", icon: PackagePlus, perm: "receipt.read" },
    { to: "/tedarikciler", label: "Tedarikçiler", icon: Building2, perm: "supplier.read" },
  ] },
  { title: "Satış & Müşteri", items: [
    { to: "/kasa", label: "Kasa / Hızlı Satış", icon: ShoppingCart, perm: "sale.create" },
    { to: "/rota-satis", label: "Sıcak Satış (Rota)", icon: Route, anyPerm: ["van.sale", "sale.create"] },
    { to: "/saha-ziyaret", label: "Saha Ziyaret & Denetim", icon: MapPin, anyPerm: ["visit.check", "visit.read"] },
    { to: "/satislar", label: "Satışlar", icon: ReceiptText, perm: "sale.read" },
    { to: "/kasalar", label: "Kasalar & Z Raporu", icon: Calculator, anyPerm: ["register.manage", "report.read", "user.assign", "location.manage", "user.manage"] },
    { to: "/musteriler", label: "Müşteriler", icon: Contact, perm: "customer.read" },
    { to: "/teklifler", label: "Teklifler", icon: FileText, perm: "customer.read" },
    { to: "/kampanyalar", label: "Kampanyalar & Sadakat", icon: Percent, anyPerm: ["price.campaign", "price.write"] },
  ] },
  { title: "Muhasebe", items: [
    { to: "/muhasebe", label: "Muhasebe Paneli", icon: Landmark, perm: "accounting.read" },
    { to: "/personel", label: "Personel & Bordro", icon: Coins, perm: "accounting.payroll" },
    { to: "/e-belge", label: "E-Belge", icon: FileText, perm: "edoc.read" },
    { to: "/belge-arsivi", label: "Belge Arşivi & Arama", icon: Archive, perm: "config.manage" },
    { to: "/bordrom", label: "Bordrom", icon: ReceiptText, perm: "dashboard.read" },
  ] },
  { title: "Analiz & Onay", items: [
    { to: "/raporlar", label: "Raporlar", icon: BarChart3, perm: "report.read" },
    { to: "/onaylar", label: "Onaylar", icon: ClipboardCheck, perm: "approve.chain" },
    { to: "/puan-odul", label: "Puan & Ödül", icon: Trophy, perm: "score.read" },
    { to: "/anketler", label: "Anketler & Öneriler", icon: MessageSquareText, anyPerm: ["survey.read", "report.read"] },
    { to: "/toptan-denetim", label: "Toptan Yetki Denetimi", icon: ShieldCheck, perm: "report.read" },
    { to: "/erisim-izi", label: "Erişim İzi (Denetim)", icon: History, perm: "approve.admin" },
    { to: "/risk-radar", label: "Risk Radar", icon: ShieldCheck, anyPerm: ["approve.admin", "report.read", "accounting.read"] },
  ] },
  { title: "Araçlar", items: [
    { to: "/firsatlar", label: "Pazar Araştırması", icon: Sparkles, perm: "purchase.read" },
    { to: "/ai", label: "AI Asistan", icon: Bot, perm: "ai.use" },
    { to: "/mesajlar", label: "Mesajlar", icon: MessageCircle, perm: "dashboard.read" },
  ] },
  { title: "Yönetim & Ayarlar", items: [
    { to: "/kullanicilar", label: "Kullanıcılar", icon: Users, perm: "user.view" },
    { to: "/roller", label: "Roller & Yetkiler", icon: ShieldCheck, perm: "role.manage" },
    { to: "/lokasyonlar", label: "Lokasyonlar", icon: MapPin, perm: "location.manage" },
    { to: "/ayarlar", label: "Sistem Ayarları", icon: Settings2, perm: "config.manage" },
  ] },
];

export default function Layout({ children }) {
  const { user, logout, can, isMobile, branding, features, feature, activeSector, setActiveSector, allowedSectors, activeSectorObj } = useAuth();
  const { activeLocId, locations: activeLocs, isGlobal: activeIsGlobal, setActive: setActiveLoc } = useActiveLocation();
  const [secOpen, setSecOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setCmdOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const [hiddenModules, setHiddenModules] = useState(new Set());
  useEffect(() => {
    api.get("/my-hidden-modules").then((r) => setHiddenModules(new Set(r.data.hidden || []))).catch(() => {});
  }, []);
  const [pendingCount, setPendingCount] = useState(0);
  const [chainPendingCount, setChainPendingCount] = useState(0);
  const [chatCount, setChatCount] = useState(0);
  const [pricingCount, setPricingCount] = useState(0);
  const [feedbackCount, setFeedbackCount] = useState(0);
  const [kdvCount, setKdvCount] = useState(0);
  const [jobRunning, setJobRunning] = useState(false);
  useEffect(() => {
    if (!can("approve.admin") && !can("approve.chain")) return;
    const fetchCount = () => api.get("/approvals/count").then((r) => setPendingCount(r.data.pending || 0)).catch(() => {});
    fetchCount();
    const t = setInterval(fetchCount, 20000);
    return () => clearInterval(t);
  }, [can]);
  useEffect(() => {
    if (!can("transfer.approve")) return;
    const f = () => api.get("/orders/chain-pending/count").then((r) => setChainPendingCount(r.data.count || 0)).catch(() => {});
    f();
    const t = setInterval(f, 20000);
    return () => clearInterval(t);
  }, [can]);
  useEffect(() => {
    const f = () => api.get("/chat/unread").then((r) => setChatCount(r.data.unread || 0)).catch(() => {});
    f();
    const t = setInterval(f, 15000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    if (!can("price.write")) return;
    const secQ = activeSector && activeSector !== "full" ? `?sector=${encodeURIComponent(activeSector)}` : "";
    const f = () => api.get(`/needs-pricing/count${secQ}`).then((r) => setPricingCount(r.data.count || 0)).catch(() => {});
    f();
    const t = setInterval(f, 60000);
    return () => clearInterval(t);
  }, [can, activeSector]);
  useEffect(() => {
    if (!can("report.read")) return;
    const f = () => api.get("/feedback/count").then((r) => setFeedbackCount(r.data.count || 0)).catch(() => {});
    f();
    const t = setInterval(f, 30000);
    return () => clearInterval(t);
  }, [can]);
  useEffect(() => {
    if (!can("price.read")) return;
    const f = () => api.get("/products/kdv/count").then((r) => setKdvCount(r.data.remaining || 0)).catch(() => {});
    f();
    const t = setInterval(f, 60000);
    return () => clearInterval(t);
  }, [can]);
  useEffect(() => {
    if (!can("config.manage") && !can("catalog.read")) return;
    const f = () => api.get("/jobs/overview").then((r) => setJobRunning(!!(r.data?.market?.running || r.data?.ai_image?.running))).catch(() => {});
    f();
    const t = setInterval(f, 15000);
    return () => clearInterval(t);
  }, [can]);
  const sectorMods = (activeSectorObj?.modules && activeSectorObj.modules.length) ? new Set(activeSectorObj.modules) : null;
  const sectorOk = (route) => !sectorMods || !SECTOR_GATED_SET.has(route) || sectorMods.has(route);
  const FLAT_NAV = NAV_GROUPS.flatMap((g) => g.items);
  const featureHidden = hiddenRoutesFromFeatures(features);
  // Pazar Araştırması (/firsatlar) sektöre özel: market ailesinde açık, diğerinde gizli (Platform override edebilir).
  const mrOk = feature("cap.market_research", isMarketFamilySector(activeSector));
  const routeOk = (to) => to !== "/firsatlar" || mrOk;
  const items = FLAT_NAV.filter((n) => can(n.perm) && !hiddenModules.has(n.to) && !featureHidden.has(n.to) && sectorOk(n.to) && routeOk(n.to));
  const paletteNav = FLAT_NAV.filter((n) => (n.anyPerm ? n.anyPerm.some((p) => can(p)) : can(n.perm)) && !hiddenModules.has(n.to) && !featureHidden.has(n.to) && sectorOk(n.to) && routeOk(n.to));
  const [openGroups, setOpenGroups] = useState(() => NAV_GROUPS.map(() => true));
  const toggleGroup = (idx) => setOpenGroups((g) => g.map((v, i) => (i === idx ? !v : v)));

  // Kenar çubuğu kaydırma konumunu koru (sayfa geçişinde başa dönmesin)
  const navRef = useRef(null);
  const location = useLocation();
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const onScroll = () => sessionStorage.setItem("groot_nav_scroll", String(el.scrollTop));
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const saved = parseInt(sessionStorage.getItem("groot_nav_scroll") || "0", 10);
    if (saved) requestAnimationFrame(() => { el.scrollTop = saved; });
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      const { data } = await api.get("/shifts/current");
      const openCarts = (data?.shift?.carts || []).filter((c) => (c.lines || []).length > 0);
      if (openCarts.length > 0) {
        toast.error("Açık sepetiniz var. Çıkış yapmadan önce satışı tamamlayın veya iptal ettirin.");
        return;
      }
    } catch (e) {}
    try {
      const mk = JSON.parse(localStorage.getItem("groot_mk_draft") || "null");
      if (mk && (mk.lines || []).length > 0) {
        toast.error("Tamamlanmamış Mal Kabul işleminiz var. Önce tamamlayın veya temizleyin.");
        return;
      }
    } catch (e) {}
    await logout();
    nav("/login");
  };

  const SideLink = ({ item, onClick }) => {
    const Icon = item.icon;
    return (
      <NavLink
        to={item.to}
        end={item.end}
        onClick={onClick}
        data-testid={`nav-${item.to === "/" ? "panel" : item.to.slice(1)}`}
        className={({ isActive }) =>
          `flex items-center gap-3 rounded-md border-l-2 px-3 py-2 text-sm font-medium transition-colors ${
            isActive
              ? "border-brand-orange bg-white/10 text-white"
              : "border-transparent text-white/65 hover:bg-white/5 hover:text-white"
          }`
        }
      >
        <Icon className="h-[18px] w-[18px]" /> <span className="flex-1">{item.label}</span>
        {item.to === "/onaylar" && pendingCount > 0 && (
          <span data-testid="nav-approvals-badge" className="ml-auto rounded-full bg-brand-orange px-1.5 py-0.5 text-[10px] font-bold text-white">{pendingCount}</span>
        )}
        {item.to === "/transferler" && chainPendingCount > 0 && (
          <span data-testid="nav-chain-pending-badge" title="Onay sıranızda bekleyen sipariş/transfer" className="ml-auto rounded-full bg-brand-orange px-1.5 py-0.5 text-[10px] font-bold text-white">{chainPendingCount}</span>
        )}
        {item.to === "/urunler" && pricingCount > 0 && (
          <span data-testid="nav-pricing-badge" title={`${pricingCount} fiyatsız ürün (fiyat bekliyor)`} className="ml-auto rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold text-white">{pricingCount}</span>
        )}
        {item.to === "/toplu-fiyat" && pricingCount > 0 && (
          <span data-testid="nav-bulkpricing-badge" title={`${pricingCount} fiyatsız ürün`} className="ml-auto rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold text-white">{pricingCount}</span>
        )}
        {item.to === "/kdv-dogrulama" && kdvCount > 0 && (
          <span data-testid="nav-kdv-badge" title={`${kdvCount} ürün KDV doğrulama bekliyor`} className="ml-auto rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold text-white">{kdvCount}</span>
        )}
        {item.to === "/mesajlar" && chatCount > 0 && (
          <span data-testid="nav-chat-badge" className="ml-auto rounded-full bg-brand-orange px-1.5 py-0.5 text-[10px] font-bold text-white">{chatCount}</span>
        )}
        {item.to === "/anketler" && feedbackCount > 0 && (
          <span data-testid="nav-feedback-badge" className="ml-auto rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white">{feedbackCount}</span>
        )}
        {item.to === "/ayarlar" && jobRunning && (
          <span data-testid="nav-jobs-running-badge" title="Arka planda bir iş çalışıyor (piyasa fiyatı / AI görsel)" className="ml-auto inline-flex items-center gap-1 rounded-full bg-blue-500 px-1.5 py-0.5 text-[9px] font-bold text-white">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> İş
          </span>
        )}
      </NavLink>
    );
  };

  const NavSections = ({ onItemClick }) => (
    <>
      {NAV_GROUPS.map((grp, gi) => {
        const visible = grp.items.filter((n) => (n.anyPerm ? n.anyPerm.some((p) => can(p)) : can(n.perm)) && !hiddenModules.has(n.to) && !featureHidden.has(n.to) && sectorOk(n.to) && routeOk(n.to));
        if (visible.length === 0) return null;
        return (
          <div key={gi} className="mb-1.5">
            {grp.title && (
              <button type="button" onClick={() => toggleGroup(gi)}
                className="mt-2 flex w-full items-center justify-between px-3 py-1 text-[11px] font-extrabold italic uppercase tracking-wider text-white/85 transition-colors hover:text-white">
                {grp.title}
                <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${openGroups[gi] ? "" : "-rotate-90"}`} />
              </button>
            )}
            {(openGroups[gi] || !grp.title) && (
              <div className="space-y-0.5">
                {visible.map((i) => <SideLink key={i.to} item={i} onClick={onItemClick} />)}
              </div>
            )}
          </div>
        );
      })}
    </>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar - desktop */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col bg-sidebar-deep lg:flex">
        <div className="flex h-16 items-center justify-between border-b border-white/10 px-4">
          <FirmLogo size={34} logoUrl={branding?.logo_url ? fileUrl(branding.logo_url) : ""} />
          <div className="flex items-center gap-1">
            <button data-testid="chat-bell" onClick={() => nav("/mesajlar")} className="relative rounded-md p-2 text-white/70 hover:bg-white/10 hover:text-white" title="Mesajlar">
              <MessageCircle className="h-5 w-5" />
              {chatCount > 0 && <span data-testid="chat-badge" className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-brand-orange px-1 text-[10px] font-bold text-white">{chatCount > 9 ? "9+" : chatCount}</span>}
            </button>
            <NotificationBell dark />
          </div>
        </div>
        <button data-testid="global-search-btn" onClick={() => setCmdOpen(true)}
          className="mx-3 mt-3 flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/60 transition-colors hover:bg-white/10 hover:text-white">
          <Search className="h-4 w-4" /> Ara…
          <span className="ml-auto rounded border border-white/15 px-1.5 py-0.5 text-[10px] font-semibold text-white/50">⌘K</span>
        </button>
        <nav ref={navRef} className="no-scrollbar flex-1 overflow-y-auto p-3">
          <NavSections />
        </nav>
        <div className="border-t border-white/10 p-3">
          <button data-testid="user-menu-toggle" onClick={() => setUserMenuOpen((v) => !v)} aria-expanded={userMenuOpen}
            className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-left transition-colors ${userMenuOpen ? "bg-white/10" : "bg-white/5 hover:bg-white/10"}`}>
            <EntityAvatar src={user?.avatar_url} name={user?.name} size={32} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-white">{user?.name}</p>
              <p className="truncate text-[11px] font-medium text-brand-orangeLight">{user?.role_label}</p>
            </div>
            <ChevronDown className={`h-4 w-4 shrink-0 text-white/50 transition-transform duration-200 ${userMenuOpen ? "rotate-180" : ""}`} />
          </button>
          {userMenuOpen && (
            <div data-testid="user-menu-panel" className="mt-1 space-y-1 fade-in">
              <button data-testid="security-btn" onClick={() => setSecOpen(true)}
                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-white/70 hover:bg-white/10 hover:text-white">
                <ShieldCheck className="h-[18px] w-[18px]" /> Güvenlik
                {user?.twofa_enabled && <span className="ml-auto rounded-sm bg-emerald-500/20 px-1.5 py-0.5 text-[10px] font-bold text-emerald-300">2FA</span>}
              </button>
              <button data-testid="logout-btn" onClick={handleLogout}
                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-white/70 hover:bg-red-500/15 hover:text-red-300">
                <LogOut className="h-[18px] w-[18px]" /> Çıkış Yap
              </button>
            </div>
          )}
        </div>
        <div className="border-t border-white/10 px-5 py-1.5" data-testid="sidebar-program-logo">
          <div className="flex w-full items-center justify-center rounded-lg bg-white/95 px-3 py-1 shadow-md" title="Program Logosu">
            <BrandLogo variant="light" size={22} showText={true} />
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-white px-4 lg:hidden">
        <FirmLogo size={30} logoUrl={branding?.logo_url ? fileUrl(branding.logo_url) : ""} />
        <div className="flex items-center gap-1">
          <button data-testid="chat-bell-mobile" onClick={() => nav("/mesajlar")} className="relative rounded-md p-2 hover:bg-slate-100" title="Mesajlar">
            <MessageCircle className="h-5 w-5 text-slate-600" />
            {chatCount > 0 && <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-brand-orange px-1 text-[10px] font-bold text-white">{chatCount > 9 ? "9+" : chatCount}</span>}
          </button>
          <NotificationBell />
          <button data-testid="global-search-btn-mobile" onClick={() => setCmdOpen(true)} className="rounded-md p-2 hover:bg-slate-100" title="Ara">
            <Search className="h-5 w-5 text-slate-600" />
          </button>
          <button data-testid="mobile-menu-btn" onClick={() => setOpen(true)} className="rounded-md p-2 hover:bg-slate-100">
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </header>

      <CommandPalette open={cmdOpen} setOpen={setCmdOpen} navItems={paletteNav} />

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 flex w-72 flex-col bg-sidebar-deep fade-in">
            <div className="flex h-14 items-center justify-between border-b border-white/10 px-4">
              <FirmLogo size={30} logoUrl={branding?.logo_url ? fileUrl(branding.logo_url) : ""} />
              <button onClick={() => setOpen(false)} className="p-2 text-white/70 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <nav className="no-scrollbar flex-1 overflow-y-auto p-3">
              <NavSections onItemClick={() => setOpen(false)} />
            </nav>
            <button data-testid="mobile-logout-btn" onClick={handleLogout}
              className="m-3 flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-red-300 hover:bg-red-500/15">
              <LogOut className="h-[18px] w-[18px]" /> Çıkış Yap
            </button>
          </div>
        </div>
      )}

      {/* Main */}
      <main className="lg:pl-64 pb-20 lg:pb-0">
        <MaintenanceBanner />
        <div className="mx-auto max-w-[1400px] p-4 sm:p-6">
          <div data-testid="active-location-bar" className="mb-4 flex flex-wrap items-center gap-2 rounded-md bg-white px-3 py-2 ring-1 ring-inset ring-slate-200">
            <MapPin className="h-4 w-4 shrink-0 text-brand-green" />
            <span className="text-xs font-medium text-slate-400">Aktif Lokasyon</span>
            {(activeIsGlobal || activeLocs.length > 1) ? (
              <div className="relative">
                <select data-testid="active-location-select" value={activeLocId} onChange={(e) => setActiveLoc(e.target.value)}
                  className="h-8 appearance-none rounded-md border border-input bg-white pl-2 pr-7 text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-green">
                  {activeIsGlobal && <option value="">Tüm Lokasyonlar</option>}
                  {activeLocs.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
                <ChevronDown className="pointer-events-none absolute right-1.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              </div>
            ) : (
              <span data-testid="active-location-name" className="text-sm font-semibold text-slate-800">{activeLocs[0]?.name || user?.location_name || "—"}</span>
            )}
            {allowedSectors && allowedSectors.length > 0 && (
              <>
                <span className="mx-1 h-4 w-px bg-slate-200" />
                <span className="text-xs font-medium text-slate-400">Sektör</span>
                {allowedSectors.length > 1 ? (
                  <div className="relative">
                    <select data-testid="active-sector-select" value={activeSector || "__all__"}
                      onChange={(e) => { const c = e.target.value; if (c === "__all__") { setActiveSector(""); return; } setActiveSector(c); const s = allowedSectors.find((x) => x.code === c); const mods = (s?.modules && s.modules.length) ? new Set(s.modules) : null; const p = location.pathname; const stillOk = !mods || !SECTOR_GATED_SET.has(p) || mods.has(p); if (!stillOk && s?.landing) nav(s.landing); }}
                      className="h-8 appearance-none rounded-md border border-input bg-white pl-2 pr-7 text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-green">
                      {user?.role === "admin" && <option value="__all__">Tüm Sektörler</option>}
                      {allowedSectors.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-1.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                  </div>
                ) : (
                  <span data-testid="active-sector-name" className="text-sm font-semibold text-slate-800">{allowedSectors[0]?.name}</span>
                )}
              </>
            )}
          </div>
          {children}
        </div>
      </main>

      {/* Mobile bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-30 flex items-center justify-around border-t border-border bg-white lg:hidden">
        {items.slice(0, 5).map((i) => {
          const Icon = i.icon;
          return (
            <NavLink key={i.to} to={i.to} end={i.end}
              data-testid={`bottomnav-${i.to === "/" ? "panel" : i.to.slice(1)}`}
              className={({ isActive }) =>
                `flex min-h-[56px] flex-1 flex-col items-center justify-center gap-0.5 text-[10px] font-medium ${
                  isActive ? "text-brand-green" : "text-slate-500"
                }`
              }>
              <Icon className="h-5 w-5" /> {i.label.split(" ")[0]}
            </NavLink>
          );
        })}
      </nav>

      <SecurityDialog open={secOpen} onOpenChange={setSecOpen} />
    </div>
  );
}
